import {
  Alert,
  Box,
  Center,
  DrawerBody,
  DrawerHeader,
  Flex,
  HStack,
  Spinner,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  VStack,
} from '@chakra-ui/react';
import { useParams } from 'react-router-dom';
import { useState, useCallback, useMemo } from 'react';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { skipToken } from '@tanstack/react-query';
import { getUserFullName } from '@duke-hq/lib';
import { UserAvatar } from '@duke-hq/ui/components/UserAvatar/UserAvatar';
import { trpcTenant } from '@duke-hq/app-provider-web/tenant/trpcTenant';
import { useTenantContext } from '@duke-hq/app-provider-web/tenant/TenantContext';

import { TeamMemberForm } from '@duke-hq/app-provider-web/tenant/Team/TeamMemberForm';
import { TeamMemberSiteAccess } from '@duke-hq/app-provider-web/tenant/Team/TeamMemberSiteAccess';
import { TeamInternalEnrolmentsList } from '@duke-hq/app-provider-web/tenant/Team/TeamInternalEnrolmentsList';
import { TeamInternalEnrolmentFormModal } from '@duke-hq/app-provider-web/tenant/Team/TeamInternalEnrolmentFormModal';
import { ConsultantRoleSchema, hasRoleAuthority } from '@duke-hq/svc-api/provider/util/consultants';
import { useAuthTenant } from '@duke-hq/app-provider-web/tenant/useAuthTenant';

export function TeamMemberDetails({
  hasInternalEnrolment = false,
}: {
  hasInternalEnrolment?: boolean;
}) {
  const { consultantId } = useParams<{ consultantId: string }>();
  const [activeTab, setActiveTab] = useState<number>(0);
  const tenantCtx = useTenantContext();
  const auth = useAuthTenant();

  const { data: member, isLoading } = trpcTenant.users.consultantById.useQuery(
    consultantId != null ? { id: consultantId } : skipToken,
    { enabled: Boolean(consultantId) },
  );

  // Check if current user can edit this member based on role hierarchy
  const canEdit = useMemo(() => {
    if (member == null || member.role == null) {
      return false;
    }

    const currentUserRole = ConsultantRoleSchema.safeParse(auth.user.role);
    const targetUserRole = ConsultantRoleSchema.safeParse(member.role);

    if (!currentUserRole.success || !targetUserRole.success) {
      return false;
    }

    return hasRoleAuthority(currentUserRole.data, targetUserRole.data);
  }, [auth.user.role, member]);

  const tabs = useMemo(
    () =>
      [
        { value: 'details', label: t`Details`, component: TeamMemberForm },
        ...(hasInternalEnrolment &&
        tenantCtx != null &&
        tenantCtx.permissions.includes('canReadInternalEnrolments') === true
          ? [
              {
                value: 'enrolments',
                label: t`Enrolments`,
                component: TeamInternalEnrolmentsList,
              },
            ]
          : []),
        ...(tenantCtx?.permissions.includes('canManageSitesAndRegions') === true
          ? [{ value: 'access', label: t`Access`, component: TeamMemberSiteAccess }]
          : []),
      ] as const,
    [tenantCtx?.permissions, hasInternalEnrolment],
  );

  const handleTabChange = useCallback(
    (index: number) => {
      if (typeof tabs[index] !== 'undefined') {
        setActiveTab(index);
      } else {
        setActiveTab(0);
      }
    },
    [tabs],
  );

  if (isLoading || !member) {
    return (
      <Center h="100vh">
        <Spinner size="xl" />
      </Center>
    );
  }

  if (member == null) {
    return (
      <Alert>
        <Trans>No consultant found</Trans>
      </Alert>
    );
  }

  return (
    <Flex flexDirection="column" w="100%" h="100%">
      <DrawerHeader
        borderBottomWidth="1px"
        borderBottomColor="border.default"
        p={{ base: 2, md: 4 }}
      >
        <HStack justify="space-between" alignContent="start">
          <HStack align="start" h="full" alignSelf="stretch">
            <UserAvatar
              user={{
                first_name: member?.first_name,
                last_name: member?.last_name,
                picture_url: member?.picture_url,
              }}
              size="lg"
            />
            <VStack ml="16px" align={'start'} spacing={0}>
              <Text fontWeight="bold">
                {getUserFullName({
                  first_name: member?.first_name,
                  last_name: member?.last_name,
                  picture_url: member?.picture_url,
                })}
              </Text>
            </VStack>
          </HStack>
          {hasInternalEnrolment === true ? (
            <Box mr={12}>
              <TeamInternalEnrolmentFormModal
                selectedMember={{
                  ...member,
                  last_name: member.last_name ?? '',
                  email: member.email ?? '',
                  role: ConsultantRoleSchema.parse(member.role),
                }}
              />
            </Box>
          ) : null}
        </HStack>
      </DrawerHeader>

      <DrawerBody p="0" border="none" m="0" paddingInline="0 !important">
        <Tabs
          isLazy
          flexDirection="column"
          display="flex"
          w="100%"
          h="100%"
          index={activeTab}
          onChange={handleTabChange}
        >
          <TabList bg="bg.subtle">
            {tabs.map((tab, index) => (
              <Tab
                key={tab.label}
                bg={activeTab === index ? 'bg.surface' : ''}
                _hover={{ bg: 'bg.muted' }}
              >
                {tab.label}
              </Tab>
            ))}
          </TabList>

          <TabPanels width="100%" height="100%" overflow="auto">
            {tabs.map(({ label, component: Component, value }) => (
              <TabPanel key={label} width="100%" height="100%" overflow="scroll" p={0}>
                {value === 'details' && !canEdit ? (
                  <Alert status="warning" borderRadius="md" m={4} w="auto">
                    <VStack align="start" w="full">
                      <Text fontWeight="semibold">
                        <Trans>View Only</Trans>
                      </Text>
                      <Text fontSize="sm">
                        <Trans>
                          You cannot edit this team member because they have a higher role level
                          than you. Only users with the same or higher role level can edit this
                          account.
                        </Trans>
                      </Text>
                    </VStack>
                  </Alert>
                ) : null}
                <Component member={member} readOnly={value === 'details' ? !canEdit : undefined} />
              </TabPanel>
            ))}
          </TabPanels>
        </Tabs>
      </DrawerBody>
    </Flex>
  );
}
